import React, { useState } from 'react';
import {
  ClipboardDocumentCheckIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';

interface ComplianceNarrative {
  id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  source_metrics: Record<string, any> | string;
  narrative: string;
  status: 'draft' | 'approved';
  approved_by?: string | null;
  approved_at?: string | null;
  created_at: string;
}

const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const parseMetrics = (m: ComplianceNarrative['source_metrics']) => {
  if (typeof m === 'string') {
    try { return JSON.parse(m); } catch { return {}; }
  }
  return m || {};
};

const ComplianceReports: React.FC = () => {
  const queryClient = useQueryClient();
  const [periodStart, setPeriodStart] = useState(startOfMonth());
  const [periodEnd, setPeriodEnd] = useState(today());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');

  const { data: narratives = [], isLoading } = useQuery({
    queryKey: ['compliance-reports'],
    queryFn: async () => {
      const res = await apiService.get('/compliance-reports');
      return (res.data || []) as ComplianceNarrative[];
    },
  });

  const selected = narratives.find((n) => n.id === selectedId) || null;
  const metrics = selected ? parseMetrics(selected.source_metrics) : null;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiService.post('/compliance-reports/generate', {
        periodStart, periodEnd, reportType: 'mfma_section71',
      });
      return res.data as ComplianceNarrative;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['compliance-reports'] });
      setSelectedId(row.id);
      setDraftText(row.narrative || '');
    },
    onError: () => alert('Failed to generate a draft. Please try again.'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selected) return null;
      const res = await apiService.patch(`/compliance-reports/${selected.id}`, { narrative: draftText });
      return res.data as ComplianceNarrative;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance-reports'] }),
    onError: () => alert('Failed to save changes — the narrative may already be approved.'),
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!selected) return null;
      const res = await apiService.post(`/compliance-reports/${selected.id}/approve`, {});
      return res.data as ComplianceNarrative;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance-reports'] }),
    onError: () => alert('Failed to approve the report. Please try again.'),
  });

  const openNarrative = (row: ComplianceNarrative) => {
    setSelectedId(row.id);
    setDraftText(row.narrative || '');
  };

  const fmtPct = (v: number | null) => (v === null || v === undefined ? 'Insufficient data' : `${v}%`);

  return (
    <div>
      <PageHeader
        title="Compliance Reports — MFMA Section 71"
        subtitle="Draft service-delivery performance narratives from live figures. Every draft requires your review and explicit sign-off before it counts as approved."
      />

      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Generate a new draft</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <CalendarIcon className="h-4 w-4 inline mr-2" />
              Period start
            </label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <CalendarIcon className="h-4 w-4 inline mr-2" />
              Period end
            </label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <Button
            variant="primary"
            icon={generateMutation.isPending ? ClockIcon : ClipboardDocumentCheckIcon}
            disabled={generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
          >
            {generateMutation.isPending ? 'Generating…' : 'Generate draft'}
          </Button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
          Figures come straight from your reports data for the selected period. The narrative paragraph is AI-drafted
          from those figures only — you can edit it freely before approving.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-sm border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Reports</h3>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : narratives.length === 0 ? (
            <EmptyState icon={ClipboardDocumentCheckIcon} title="No reports yet" description="Generate your first draft above." />
          ) : (
            <div className="space-y-2">
              {narratives.map((row) => (
                <button
                  key={row.id}
                  onClick={() => openNarrative(row)}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${
                    selectedId === row.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {new Date(row.period_start).toLocaleDateString()} → {new Date(row.period_end).toLocaleDateString()}
                    </span>
                    <Badge tone={row.status === 'approved' ? 'success' : 'neutral'}>
                      {row.status === 'approved' ? 'Approved' : 'Draft'}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.report_type}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {!selected ? (
            <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <EmptyState icon={ClipboardDocumentCheckIcon} title="No report selected" description="Generate a draft or pick one from the list to review it." />
            </div>
          ) : (
            <>
              {metrics && (
                <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    Source figures
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Total reports</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{metrics.totalReports}</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Resolution rate</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmtPct(metrics.resolutionRatePct)}</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">SLA compliance</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmtPct(metrics.slaCompliancePct)}</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Open at period end</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{metrics.openAtPeriodEnd}</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Avg resolution time</div>
                      <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {metrics.avgResolutionHours !== null ? `${metrics.avgResolutionHours}h` : 'N/A'}
                      </div>
                    </div>
                  </div>
                  {Array.isArray(metrics.topCategories) && metrics.topCategories.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Top categories</div>
                      <div className="flex flex-wrap gap-2">
                        {metrics.topCategories.map((c: any, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                            {c.category} ({c.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-accent-600" />
                  Narrative {selected.status === 'draft' ? '(editable)' : ''}
                </h2>
                {selected.status === 'draft' ? (
                  <textarea
                    value={draftText}
                    onChange={(e) => setDraftText(e.target.value)}
                    rows={8}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="No AI narrative was generated — write the narrative for this period here before approving."
                  />
                ) : (
                  <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">{selected.narrative}</p>
                )}

                {selected.status === 'draft' ? (
                  <div className="flex gap-3 mt-4">
                    <Button
                      icon={saveMutation.isPending ? ClockIcon : PencilSquareIcon}
                      disabled={saveMutation.isPending}
                      onClick={() => saveMutation.mutate()}
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save changes'}
                    </Button>
                    <Button
                      variant="primary"
                      icon={approveMutation.isPending ? ClockIcon : CheckCircleIcon}
                      disabled={approveMutation.isPending || !draftText.trim()}
                      onClick={() => {
                        if (window.confirm('Approve this compliance report? This locks the narrative and records your sign-off.')) {
                          approveMutation.mutate();
                        }
                      }}
                    >
                      {approveMutation.isPending ? 'Approving…' : 'Approve & sign off'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-4 text-sm text-green-700 dark:text-green-400">
                    <CheckCircleIcon className="h-5 w-5" />
                    Approved {selected.approved_at ? new Date(selected.approved_at).toLocaleString() : ''}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReports;
